// src/components/purchase/DetalleCompraModal.js
// Modal de detalle de compra: factura + impuestos + recepciones

import React, { useState, useEffect } from 'react';
import { Modal, Table, Spinner, Button } from 'react-bootstrap';
import { getPurchaseDetail } from '../../services/purchaseApi';
import { getTaxes } from '../../services/taxApi';
import { getDueDate } from '../../utils/purchaseUtils';
import MarcarPagadaModal from './MarcarPagadaModal';
import './DetalleCompraModal.css';

const formatterCOP = new Intl.NumberFormat('es-CO', {
  style: 'currency',
  currency: 'COP',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0
});

const DetalleCompraModal = ({ show, onHide, purchaseId, onRegistrarStock, onRefresh }) => {
  const [detail, setDetail] = useState(null);
  const [taxDefinitions, setTaxDefinitions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [showMarcarPagadaModal, setShowMarcarPagadaModal] = useState(false);

  useEffect(() => {
    const load = async () => {
      if (!show || !purchaseId) return;
      setLoading(true);
      setErrorMsg('');
      setDetail(null);
      const [detailRes, taxesRes] = await Promise.all([
        getPurchaseDetail(purchaseId),
        getTaxes({ scope: 'compra' })
      ]);
      if (detailRes.ok) setDetail(detailRes.data);
      else setErrorMsg('No se pudo cargar el detalle.');
      if (taxesRes?.ok) {
        const list = taxesRes.data?.message ?? taxesRes.data;
        setTaxDefinitions(Array.isArray(list) ? list : []);
      }
      setLoading(false);
    };
    load();
  }, [show, purchaseId]);

  const handleRegistrarStock = () => {
    if (onRegistrarStock && purchaseId) {
      onRegistrarStock(purchaseId);
    } else {
      onHide();
    }
  };

  const receipts = detail?.stock_receipts || [];
  const taxes = detail?.purchase_taxes || [];
  const status = (detail?.status || detail?.status_name || '').toUpperCase();
  const dueDate = getDueDate(detail);
  const isOverdue =
    status === 'PENDIENTE_DE_PAGO' && dueDate && new Date(dueDate) < new Date();
  const canMarkAsPaid = status === 'PENDIENTE_DE_PAGO';

  const getStatusBadgeClass = () => {
    if (isOverdue) return 'vencida';
    if (status === 'PAGADA') return 'pagada';
    if (status === 'PENDIENTE_DE_PAGO') return 'pendiente';
    if (status === 'ACTIVO' || status === 'COMPLETO') return 'pagada';
    return 'pendiente';
  };

  const handleMarkAsPaidSuccess = () => {
    setShowMarcarPagadaModal(false);
    if (onRefresh) onRefresh();
    if (purchaseId) {
      getPurchaseDetail(purchaseId).then((res) => {
        if (res.ok) setDetail(res.data);
      });
    }
  };
  const getTaxRate = (t) => {
    const fromTax = t.tax_rate ?? t.rate;
    if (fromTax != null && fromTax !== '' && parseFloat(fromTax) !== 0) return parseFloat(fromTax);
    const def = taxDefinitions.find((d) => String(d.id_tax ?? d.id) === String(t.id_tax));
    const r = def?.rate ?? def?.tax_rate;
    return r != null && r !== '' ? parseFloat(r) : null;
  };

  return (
    <>
    <Modal
      show={show}
      onHide={onHide}
      centered
      size="lg"
      className="detalle-compra-modal"
      backdrop="static"
    >
      <Modal.Header className="detalle-compra-modal-header">
        <Modal.Title>Detalle compra #{purchaseId || '-'}</Modal.Title>
        <button type="button" className="btn-close btn-close-white" aria-label="Cerrar" onClick={onHide} />
      </Modal.Header>
      <Modal.Body className="detalle-compra-modal-body">
        {loading ? (
          <div className="detalle-compra-loading">
            <Spinner animation="border" style={{ color: '#EA9D0E' }} />
          </div>
        ) : errorMsg ? (
          <div className="detalle-compra-error">{errorMsg}</div>
        ) : detail ? (
          <>
            <div className="detalle-compra-info">
              <div className="detalle-compra-info-grid">
                <div className="detalle-compra-info-item">
                  <span className="detalle-compra-label">Fecha</span>
                  <span className="detalle-compra-value">{detail.purchase_date || '-'}</span>
                </div>
                <div className="detalle-compra-info-item">
                  <span className="detalle-compra-label">Proveedor</span>
                  <span className="detalle-compra-value">{detail.supplier?.name || '-'}</span>
                </div>
                <div className="detalle-compra-info-item">
                  <span className="detalle-compra-label">Referencia</span>
                  <span className="detalle-compra-value">{detail.reference_doc || '-'}</span>
                </div>
                <div className="detalle-compra-info-item">
                  <span className="detalle-compra-label">Método de pago</span>
                  <span className="detalle-compra-value">{detail.payment_method?.method_name || '-'}</span>
                </div>
                <div className="detalle-compra-info-item">
                  <span className="detalle-compra-label">Términos</span>
                  <span className="detalle-compra-value">
                    {detail.terms_type?.terms_name
                      ? `${detail.terms_type.terms_name}${detail.terms_type.days != null ? ` (${detail.terms_type.days} días)` : ''}`
                      : '-'}
                  </span>
                </div>
                <div className="detalle-compra-info-item">
                  <span className="detalle-compra-label">Vencimiento</span>
                  <span
                    className={`detalle-compra-value ${isOverdue ? 'detalle-compra-overdue' : ''}`}
                  >
                    {dueDate || '-'}
                  </span>
                </div>
                <div className="detalle-compra-info-item">
                  <span className="detalle-compra-label">Estado</span>
                  <span className={`detalle-compra-badge ${getStatusBadgeClass()}`}>
                    {detail.status || detail.status_name || 'pendiente'}
                  </span>
                </div>
              </div>
              {detail.notes && (
                <div className="detalle-compra-notes">
                  <span className="detalle-compra-label">Notas</span>
                  <span className="detalle-compra-value">{detail.notes}</span>
                </div>
              )}
            </div>

            <div className="detalle-compra-totales">
              <div className="detalle-compra-total-row">
                <span>Subtotal</span>
                <span>{formatterCOP.format(detail.subtotal ?? 0)}</span>
              </div>
              <div className="detalle-compra-total-row">
                <span>Descuento</span>
                <span>{formatterCOP.format(detail.total_discount ?? 0)}</span>
              </div>
              <div className="detalle-compra-total-row">
                <span>Impuestos</span>
                <span>{formatterCOP.format(detail.total_tax ?? 0)}</span>
              </div>
              <div className="detalle-compra-total-row detalle-compra-total-final">
                <span>Total a pagar</span>
                <span>{formatterCOP.format(detail.grand_total ?? 0)}</span>
              </div>
            </div>

            {taxes.length > 0 && (
              <div className="detalle-compra-section">
                <h6 className="detalle-compra-section-title">Impuestos</h6>
                <Table size="sm" className="detalle-compra-table">
                  <thead>
                    <tr>
                      <th>Impuesto</th>
                      <th>Tasa %</th>
                      <th>Valor</th>
                    </tr>
                  </thead>
                  <tbody>
                    {taxes.map((t) => {
                      const rateNum = getTaxRate(t);
                      const rateDisplay = rateNum != null ? (rateNum < 1 ? `${(rateNum * 100).toFixed(1)}%` : `${rateNum}%`) : '-';
                      return (
                        <tr key={t.id_tax}>
                          <td>{t.tax_description || t.description || '-'}</td>
                          <td>{rateDisplay}</td>
                          <td>{formatterCOP.format(t.tax_amount ?? 0)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </div>
            )}

            {canMarkAsPaid && (
              <div className="detalle-compra-section detalle-compra-marcar-pagada">
                <Button
                  size="sm"
                  className="detalle-compra-btn-pagar"
                  onClick={() => setShowMarcarPagadaModal(true)}
                >
                  Marcar como pagada
                </Button>
              </div>
            )}

            <div className="detalle-compra-section">
              <div className="detalle-compra-section-header">
                <h6 className="detalle-compra-section-title">Recepciones de stock</h6>
                <Button size="sm" className="detalle-compra-btn-stock" onClick={handleRegistrarStock}>
                  Registrar stock
                </Button>
              </div>
              {receipts.length === 0 ? (
                <p className="detalle-compra-empty">No hay recepciones. Usa &quot;Registrar stock&quot; para agregar ítems.</p>
              ) : (
                <Table size="sm" className="detalle-compra-table">
                  <thead>
                    <tr>
                      <th>SKU</th>
                      <th>Descripción</th>
                      <th>Cant.</th>
                      <th>Costo unit.</th>
                      <th>Descuento</th>
                      <th>Fecha</th>
                    </tr>
                  </thead>
                  <tbody>
                    {receipts.map((r) => (
                      <tr key={r.id_stock_receipt || `${r.id_item}-${r.receipt_date}`}>
                        <td>{r.item?.sku || '-'}</td>
                        <td>{r.item?.description || '-'}</td>
                        <td>{r.quantity ?? 0}</td>
                        <td>{formatterCOP.format(r.unit_cost ?? 0)}</td>
                        <td>{formatterCOP.format(r.discount_amount ?? 0)}</td>
                        <td>{r.receipt_date || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </div>
          </>
        ) : null}
      </Modal.Body>
    </Modal>
    <MarcarPagadaModal
      show={showMarcarPagadaModal}
      onHide={() => setShowMarcarPagadaModal(false)}
      purchaseId={purchaseId}
      purchaseReference={detail?.reference_doc}
      onSuccess={handleMarkAsPaidSuccess}
    />
    </>
  );
};

export default DetalleCompraModal;
